
































import Vue from "vue";

import IconChevronDown from "../../assets/icons/transparent/ChevronDown.svg";
import { FormOption } from "../../models/control";

// Local components
import JzIcon from "../image/JzIcon.vue";
import JzList from "../layout/JzList.vue";
import JzListItem from "../layout/JzListItem.vue";

// Convenient constants
const DEFAULT_LABEL = "--";
const CONTENT_CLOSED = false;

interface Data {
  iconChevronDown: string;
  isOpened: boolean;
  formerStyling?: {
    bodyPosition: string | null;
  };
}

interface Methods {
  select: (val: string | number) => void;
  toggleContent: (forcedStatus?: boolean) => void;
  handleOutsideClick: (e: MouseEvent) => void;
}

interface Computed {
  selectedLabel: string;
}

interface Props {
  // Optional selector of a container to remove the overflow behaviour upon
  // backdrop display. Such selector can be an ID or a CSS class. In case
  // if a CSS class, only the first match is considered.
  containerSelector: string;
  // Label to display when no option is selected
  defaultLabel: string;
  disabled: boolean;
  icon: string;
  label: string;
  opened: boolean;
  options: FormOption<string | number>[];
  value: string | number;
}

/**
 * Note: this is no @click listener on .dropdown-backdrop because such click is
 * also handled by the `handleOutsideClick` listener
 */
export default Vue.extend<Data, Methods, Computed, Props>({
  name: "JzInputDropdown",
  components: { JzIcon, JzList, JzListItem },

  props: {
    containerSelector: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    defaultLabel: { type: String, default: DEFAULT_LABEL },
    icon: { type: String, default: "" },
    label: { type: String, default: "" },
    opened: { type: Boolean, default: false },
    options: {
      type: Array as () => FormOption<string | number>[],
      required: true
    },
    value: { type: [String, Number], default: "" }
  },

  data: function() {
    return {
      iconChevronDown: IconChevronDown,
      isOpened: this.opened,
      overflowOldValues: undefined
    };
  },

  computed: {
    selectedLabel() {
      return (
        this.options
          .filter(opt => opt.value === this.value)
          .map(opt => opt.label)
          .find(() => true) || this.defaultLabel
      );
    }
  },

  watch: {
    isOpened: {
      /**
       * Although the event listener addition / removal could be moved to
       * toggleContent` method, do not remove this watcher in case the
       * `isOpened` data is modified:
       *  - directly by another method
       *  - directly from another component
       */
      handler: function(newVal) {
        if (newVal) {
          document.addEventListener("click", this.handleOutsideClick);
        } else {
          document.removeEventListener("click", this.handleOutsideClick);
        }
      },
      immediate: true
    }
  },

  destroyed() {
    document.removeEventListener("click", this.handleOutsideClick);
  },

  methods: {
    // Selecting an element automatically closes the content
    select(optionValue: string | number) {
      this.$emit("input", optionValue);
      this.toggleContent(CONTENT_CLOSED);
    },

    /**
     * When toggling, the dropdown list can appear in modal mode. In modal mode,
     * the scroll of the page must be disabled. Special Safari mention: to disable
     * the "bouncing", the body must have a special position
     *
     * @param forcedStatus force a state of the isOpened flag
     * @see https://css-tricks.com/prevent-page-scrolling-when-a-modal-is-open/
     */
    async toggleContent(forcedStatus?: boolean) {
      // Toggle opened flag
      this.isOpened = forcedStatus || !this.isOpened;

      // If there is no provided container selector, there is no overflow to disable
      if (!this.containerSelector) {
        return;
      }

      // If the containerSelector is an ID, search for it
      let containerElement = document.getElementById(this.containerSelector);
      if (!containerElement) {
        // Otherwise search by CSS classes...
        const searchByClasses = document.getElementsByClassName(
          this.containerSelector
        );
        // ... and take the first one if applicable
        if (searchByClasses) {
          containerElement = searchByClasses[0] as HTMLElement;
        }
      }
      // No container is found so there is no overflow to disable
      if (!containerElement) {
        return;
      }

      // Ensure that the backdrop element is found
      const backdrop = this.$el.querySelector(".dropdown-backdrop");
      if (!backdrop) {
        return;
      }

      // Ensure that the component is updated before checking the backdrop display
      await this.$nextTick();

      // Fetch the updated (important!) backdrop styling
      const backdropDisplay = window.getComputedStyle(backdrop, null).display;

      const contentElmt = this.$el.querySelector(".dropdown-content");
      if (contentElmt) {
        (contentElmt as HTMLElement).style.pointerEvents = "initial";
      }

      // If the modal is opened AND the backdrop is visible, then we are inside a mobile view
      if (this.isOpened && backdropDisplay !== "none") {
        document.body.style.pointerEvents = "none";

        // Save the current overflow values to restore it later
        this.formerStyling = {
          bodyPosition: document.body.style.position
        };

        document.body.style.position = "fixed";
      }
      // Restore former styling if available
      else if (this.formerStyling) {
        document.body.style.pointerEvents = "initial";

        document.body.style.position = this.formerStyling.bodyPosition || "";

        this.formerStyling = undefined;
      }
    },

    handleOutsideClick(e: MouseEvent) {
      const isClickInside = (e: MouseEvent, el: Element): boolean => {
        const rect = el.getBoundingClientRect();
        return (
          e.x >= rect.left &&
          e.x <= rect.right &&
          e.y >= rect.top &&
          e.y <= rect.bottom
        );
      };
      // Because of its position absolute/fixed, the dropdown list must be
      // checked separately
      const contentElmt = this.$el.querySelector(".dropdown-content");

      const isInsideDisplay = isClickInside(e, this.$el);
      const isInsideContent = contentElmt
        ? isClickInside(e, contentElmt)
        : false;

      if (!isInsideDisplay && !isInsideContent) {
        this.toggleContent(CONTENT_CLOSED);
      }
    }
  }
});
